/**
 * Group Join Service
 *
 * Single entry point for resolving join/invitation tokens per the Hierarchical Join Link System plan.
 * Token format uses prefixes for resolution order: d_ (designation), i_ (invitation), g_ (group/invite code).
 * One route /join/:token delegates here; then app redirects to existing flows or shows unified join UI.
 *
 * @see hierarchical_join_link_system_ce8e8d3e.plan.md
 */

import { db } from './firebase';
import { getDesignation } from './staffManagementService';

/** Token type constants */
export const JOIN_TOKEN_TYPE = {
  DESIGNATION: 'designation',
  INVITATION: 'invitation',
  INVITE_CODE: 'inviteCode',
  TEMPLATE: 'template',
  REGIONAL_TEMPLATE: 'regionalTemplate',
  LOTSOG_INVITATION: 'lotsogInvitation',
};

/** Prefixes per plan: d_ = designation, i_ = invitation, t_ = PET, r_ = regional template, g_ or none = invite code / group link */
const PREFIX_DESIGNATION = 'd_';
const PREFIX_INVITATION = 'i_';
const PREFIX_TEMPLATE = 't_';
const PREFIX_REGIONAL = 'r_';
const LOTSOG_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

/**
 * Resolve an invitation by ID (Firestore invitations collection)
 * @param {string} invitationId
 * @returns {Promise<Object|null>} { id, ...invitationData } or null
 */
export async function getInvitation(invitationId) {
  if (!invitationId) return null;
  try {
    const doc = await db.collection('invitations').doc(invitationId).get();
    if (!doc.exists) return null;
    return { id: doc.id, ...doc.data() };
  } catch (err) {
    console.error('[GroupJoinService] getInvitation error:', err);
    return null;
  }
}

/**
 * Resolve a join token to type and payload.
 * - d_<id> → designation (staffDesignations)
 * - i_<id> → invitation (invitations)
 * - no prefix or g_* → inviteCode (ChartMind invite code; app resolves via chartmindAdminService)
 *
 * @param {string} token - Raw token from URL /join/:token
 * @returns {Promise<{ type: string, payload: Object }|null>} Resolution result or null if invalid
 */
export async function resolveToken(token) {
  if (!token || typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith(PREFIX_DESIGNATION)) {
    const designationId = trimmed.slice(PREFIX_DESIGNATION.length);
    const designation = await getDesignation(designationId);
    if (!designation) return null;
    return {
      type: JOIN_TOKEN_TYPE.DESIGNATION,
      payload: { designationId: designation.id, ...designation },
    };
  }

  if (trimmed.startsWith(PREFIX_INVITATION)) {
    const invitationId = trimmed.slice(PREFIX_INVITATION.length);
    const invitation = await getInvitation(invitationId);
    if (!invitation) return null;
    return {
      type: JOIN_TOKEN_TYPE.INVITATION,
      payload: { invitationId: invitation.id, ...invitation },
    };
  }

  // t_ prefix: ChartMind Patient Encounter Template invite code
  if (trimmed.startsWith(PREFIX_TEMPLATE)) {
    const templateCode = trimmed.slice(PREFIX_TEMPLATE.length);
    return {
      type: JOIN_TOKEN_TYPE.TEMPLATE,
      payload: { code: templateCode },
    };
  }

  // r_ prefix: Regional ChartMind Template invite code (P2 prompts)
  if (trimmed.startsWith(PREFIX_REGIONAL)) {
    const regionalCode = trimmed.slice(PREFIX_REGIONAL.length);
    return {
      type: JOIN_TOKEN_TYPE.REGIONAL_TEMPLATE,
      payload: { code: regionalCode },
    };
  }

  // Legacy join code (no prefix) or future g_ group link: treat as invite code
  // App will resolve via chartmindAdminService.findAdminByInviteCode(code)
  if (LOTSOG_TOKEN_PATTERN.test(trimmed)) {
    return {
      type: JOIN_TOKEN_TYPE.LOTSOG_INVITATION,
      payload: { token: trimmed },
    };
  }

  return {
    type: JOIN_TOKEN_TYPE.INVITE_CODE,
    payload: { code: trimmed },
  };
}
